import { useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Gauge, Timer, Fuel, WifiOff, MapPin, Clock, Power, Layers, ChevronLeft, ChevronRight, Check, X, Battery, Bell, Mail, Settings2 } from "lucide-react";

const INP = "bg-secondary/60 border border-border rounded-md px-3 py-2 text-sm outline-none focus:border-[#17A2B8] w-full";
const BIG = "bg-secondary/60 border border-border rounded-md px-4 py-3 text-2xl font-bold outline-none focus:border-[#17A2B8] w-32 text-center";
const DAYS = [[0, "Lun"], [1, "Mar"], [2, "Mer"], [3, "Jeu"], [4, "Ven"], [5, "Sam"], [6, "Dim"]];

// Modèles de surveillance (le mapping vers le moteur existant est purement interne, aucun calcul modifié).
// Les 5 situations les plus courantes sont présentées en premier (groupe « common »).
const TEMPLATES = [
  { key: "exces_vitesse", engine: "native", icon: Gauge, title: "Excès de vitesse", desc: "Être alerté lorsqu'un véhicule dépasse une vitesse définie", common: true },
  { key: "zone", engine: "a9", icon: MapPin, title: "Entrée / sortie de zone", desc: "Surveiller la présence ou l'absence d'un véhicule dans une zone", common: true },
  { key: "horaires", engine: "a9", icon: Clock, title: "Utilisation hors horaires", desc: "Être alerté lorsqu'un véhicule se déplace en dehors des horaires autorisés (remorquage contact coupé inclus)", common: true },
  { key: "batterie", engine: "native", icon: Battery, title: "Alimentation externe faible", desc: "Être alerté lorsque la tension d'alimentation externe (batterie du véhicule) passe sous un seuil défini. Distinct de la batterie de secours interne du traceur.", common: true },
  { key: "deconnexion", engine: "native", icon: WifiOff, title: "Absence de communication", desc: "Être alerté lorsqu'un traceur ne communique plus", common: true },
  { key: "arret_prolonge", engine: "native", icon: Timer, title: "Arrêt prolongé", desc: "Être alerté lorsqu'un véhicule reste arrêté trop longtemps" },
  { key: "ralenti", engine: "native", icon: Fuel, title: "Ralenti prolongé", desc: "Être alerté lorsqu'un moteur tourne au ralenti trop longtemps" },
  { key: "contact", engine: "a9", icon: Power, title: "Contact moteur", desc: "Surveiller la mise ou la coupure du contact moteur" },
  { key: "custom", engine: "advanced", icon: Layers, title: "Règle personnalisée", desc: "Combiner plusieurs conditions (mode avancé)" },
];

const SOURCE_LABEL = {
  "vehicle.max_speed_kmh": "Définie dans la fiche du véhicule",
  "alert_setting:vehicle": "Réglage spécifique à ce véhicule",
  "alert_setting:pool": "Définie pour ce groupe",
  "alert_setting:company": "Définie pour cette société",
  "alert_setting:global": "Réglage global",
  "default": "Valeur par défaut",
};

const STEP_LABELS = ["Situation", "Véhicules", "Avertissement"];
// Type de règle de notification correspondant (A6). Les règles A9 (zone/horaires/contact) sont des épisodes « composee ».
const NOTIF_TYPE = { exces_vitesse: "exces_vitesse", arret_prolonge: "arret_prolonge", ralenti: "ralenti", batterie: "batterie", deconnexion: "deconnexion", zone: "composee", horaires: "composee", contact: "composee" };

export default function SimpleAlertWizard({ open, onClose, onCreated, onAdvanced, companies = [], pools = [], vehicles = [], zones = [], isAdmin = false }) {
  const [step, setStep] = useState(0);
  const [tpl, setTpl] = useState(null);
  const [scopeKind, setScopeKind] = useState(isAdmin ? "fleet" : "company"); // fleet|company|pool|vehicle
  const [scopeId, setScopeId] = useState("");
  const [cfg, setCfg] = useState({});
  const [effective, setEffective] = useState(null); // {value, source}
  const [saving, setSaving] = useState(false);
  const [advOpen, setAdvOpen] = useState(false);
  // Avertissement (réutilise le mécanisme de notification existant /alert-notification-rules)
  const [notif, setNotif] = useState({ in_app: true, email: false, roles: ["admin", "gestionnaire"] });
  // Pré-remplissage de la config de notification STOCKÉE (pour NE PAS écraser les champs non modifiés).
  const [notifLoaded, setNotifLoaded] = useState(false);
  const prefilledRef = useRef(null);

  const reset = useCallback(() => {
    setStep(0); setTpl(null); setScopeKind(isAdmin ? "fleet" : "company"); setScopeId(""); setCfg({}); setEffective(null);
    setAdvOpen(false); setNotif({ in_app: true, email: false, roles: ["admin", "gestionnaire"] });
    setNotifLoaded(false); prefilledRef.current = null;
  }, [isAdmin]);

  useEffect(() => { if (open) reset(); }, [open, reset]);

  const scopeMap = { fleet: "global", company: "company", pool: "pool", vehicle: "vehicle" };
  const backendScope = () => {
    if (scopeKind === "fleet") {
      if (isAdmin) return { scope_type: "global", scope_id: null };
      return { scope_type: "company", scope_id: companies[0]?.id || null };
    }
    return { scope_type: scopeMap[scopeKind], scope_id: scopeId };
  };
  const fleetAvailable = isAdmin || companies.length === 1;

  const loadEffective = useCallback(async () => {
    if (!tpl || tpl.key !== "exces_vitesse") return;
    const { scope_type, scope_id } = backendScope();
    if (scope_type !== "global" && !scope_id) { setEffective(null); return; }
    try {
      const qs = `scope_type=${scope_type}` + (scope_id ? `&scope_id=${encodeURIComponent(scope_id)}` : "");
      const r = await api.get(`/alert-settings?${qs}`);
      const row = (r.data || []).find((x) => x.type === "exces_vitesse");
      if (row) {
        const eff = row.effective_speed_limit_kmh != null ? row.effective_speed_limit_kmh : (row.params?.speed_limit_kmh ?? 90);
        setEffective({ value: eff, source: row.speed_limit_source, hasOverride: row.has_override });
      }
    } catch { setEffective(null); }
  }, [tpl, scopeKind, scopeId]);

  // Étape 2 (Avertissement) : on charge le seuil effectif pour l'excès de vitesse.
  useEffect(() => { if (open && step === 2) loadEffective(); }, [open, step, loadEffective]);

  // Charge la config de notification STOCKÉE à la portée+type exacts. Pré-remplit l'UI (une seule fois
  // par portée+type) quand un override existe, pour que l'utilisateur voie l'état réel et que la
  // sauvegarde ne réinitialise pas des canaux/rôles préexistants avec les valeurs par défaut du wizard.
  const loadExistingNotif = useCallback(async () => {
    const ntype = NOTIF_TYPE[tpl?.key];
    if (!ntype) { setNotifLoaded(true); return; }
    const { scope_type, scope_id } = backendScope();
    if (scope_type !== "global" && !scope_id) { setNotifLoaded(true); return; }
    const key = `${scope_type}:${scope_id || ""}:${ntype}`;
    const fresh = prefilledRef.current !== key;
    if (fresh) {
      setNotifLoaded(false);
      // Nouvelle portée+type : repartir des valeurs par défaut (évite qu'un pré-remplissage d'une
      // portée précédente ne subsiste sur une portée SANS override).
      setNotif({ in_app: true, email: false, roles: ["admin", "gestionnaire"] });
    }
    try {
      const qs = `scope_type=${scope_type}` + (scope_id ? `&scope_id=${encodeURIComponent(scope_id)}` : "");
      const r = await api.get(`/alert-notification-rules?${qs}`);
      const row = (r.data || []).find((x) => x.type === ntype) || null;
      if (row && row.has_override && fresh) {
        setNotif({ in_app: !!row.channels?.in_app, email: !!row.channels?.email, roles: row.recipient_roles || [] });
      }
      prefilledRef.current = key;
      setNotifLoaded(true);
    } catch { setNotifLoaded(true); }
  }, [tpl, scopeKind, scopeId]);

  useEffect(() => { if (open && step === 2) loadExistingNotif(); }, [open, step, loadExistingNotif]);

  if (!open) return null;

  const pickTemplate = (t) => {
    if (t.engine === "advanced") { onClose(); onAdvanced && onAdvanced(); return; }
    setTpl(t);
    const d = { severity: "medium", always_on: true, days: [0, 1, 2, 3, 4, 5, 6], hour_start: 7, hour_end: 18, duration_min: 0 };
    if (t.key === "exces_vitesse") d.speed_limit_kmh = "";
    if (t.key === "arret_prolonge") d.minutes = 15;
    if (t.key === "ralenti") d.minutes = 5;
    if (t.key === "batterie") { d.open_v = 11.0; d.close_v = 11.5; }
    if (t.key === "deconnexion") d.minutes = 5;
    if (t.key === "zone") { d.zone_id = ""; d.presence = "outside"; }
    if (t.key === "horaires") { d.days = [0, 1, 2, 3, 4]; d.hour_start = 7; d.hour_end = 18; }
    if (t.key === "contact") d.ignition = "on";
    setCfg(d);
    setAdvOpen(false);
    setStep(1);
  };

  const scopeLabelText = () => {
    if (scopeKind === "fleet") return "toute la flotte";
    if (scopeKind === "company") return "la société « " + (companies.find((c) => c.id === scopeId)?.name || "?") + " »";
    if (scopeKind === "pool") return "le groupe « " + (pools.find((p) => p.id === scopeId)?.name || "?") + " »";
    return "le véhicule « " + (vehicles.find((v) => v.id === scopeId)?.name || "?") + " »";
  };

  const usesSchedule = tpl && tpl.engine === "native";
  const usesDuration = tpl && ["zone", "contact"].includes(tpl.key);

  const scheduleClause = () => {
    if (cfg.always_on) return " — 24h/24 et 7j/7";
    const FULL = ["lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi", "dimanche"];
    const ds = [...(cfg.days || [])].sort((a, b) => a - b);
    const h = (x) => `${String(x).padStart(2, "0")}h00`;
    let daysTxt;
    if (ds.length === 0) daysTxt = "";
    else if (ds.length === 7) daysTxt = "tous les jours";
    else if (ds.length === 1) daysTxt = `le ${FULL[ds[0]]}`;
    else if (ds.every((v, i) => i === 0 || v === ds[i - 1] + 1)) daysTxt = `du ${FULL[ds[0]]} au ${FULL[ds[ds.length - 1]]}`;
    else daysTxt = `les ${ds.map((d) => FULL[d]).join(", ")}`;
    return `${daysTxt ? ", " + daysTxt : ""} entre ${h(cfg.hour_start)} et ${h(cfg.hour_end)}`;
  };

  const summary = () => {
    if (!tpl) return "";
    const who = scopeKind === "fleet" ? "les véhicules de la flotte" :
      scopeKind === "company" ? "les véhicules de " + scopeLabelText() :
      scopeKind === "pool" ? "les véhicules de " + scopeLabelText() : "le véhicule " + scopeLabelText().replace("le véhicule ", "");
    const sc = tpl.engine === "native" ? scheduleClause() : "";
    switch (tpl.key) {
      case "exces_vitesse": {
        const lim = cfg.speed_limit_kmh || (effective ? effective.value : 90);
        return `Vous serez alerté lorsque ${who} dépassera ${lim} km/h${sc}.`;
      }
      case "arret_prolonge": return `Vous serez alerté lorsque ${who} restera à l'arrêt plus de ${cfg.minutes} minutes${sc}.`;
      case "ralenti": return `Vous serez alerté lorsque le moteur de ${who} tournera au ralenti plus de ${cfg.minutes} minutes${sc}.`;
      case "batterie": return `Vous serez alerté lorsque la tension d'alimentation externe de ${who} descendra sous ${cfg.open_v} V${sc}.`;
      case "deconnexion": return `Vous serez alerté lorsque le traceur de ${who} ne communiquera plus depuis ${cfg.minutes} minutes${sc}.`;
      case "zone": { const z = zones.find((x) => x.id === cfg.zone_id); return `Vous serez alerté lorsque ${who} sera ${cfg.presence === "outside" ? "hors de" : "dans"} la zone « ${z ? z.name : "?"} ».`; }
      case "horaires": return `Vous serez alerté en cas de déplacement concernant ${who} en dehors des horaires autorisés (${String(cfg.hour_start).padStart(2, "0")}h00–${String(cfg.hour_end).padStart(2, "0")}h00).`;
      case "contact": return `Vous serez alerté lorsque le contact moteur de ${who} sera ${cfg.ignition === "on" ? "mis" : "coupé"}.`;
      default: return "";
    }
  };

  const validReglages = () => {
    if (!tpl) return false;
    if (tpl.key === "exces_vitesse") return true;
    if (["arret_prolonge", "ralenti", "deconnexion"].includes(tpl.key)) return Number(cfg.minutes) > 0;
    if (tpl.key === "batterie") return Number(cfg.open_v) > 0;
    if (tpl.key === "zone") return !!cfg.zone_id;
    if (tpl.key === "horaires") return cfg.days?.length > 0;
    if (tpl.key === "contact") return true;
    return false;
  };

  const scopeChosen = scopeKind === "fleet" || !!scopeId;

  const buildNativeBody = () => {
    const body = { active: true, always_on: !!cfg.always_on };
    if (!cfg.always_on) { body.days = cfg.days || []; body.hour_start = Number(cfg.hour_start); body.hour_end = Number(cfg.hour_end); }
    if (tpl.key === "exces_vitesse") { if (cfg.speed_limit_kmh) body.params = { speed_limit_kmh: Number(cfg.speed_limit_kmh) }; }
    if (tpl.key === "arret_prolonge") body.params = { min_duration_s: Number(cfg.minutes) * 60 };
    if (tpl.key === "ralenti") body.params = { min_duration_s: Number(cfg.minutes) * 60 };
    if (tpl.key === "batterie") body.params = { open_below_mv: Math.round(Number(cfg.open_v) * 1000), close_above_mv: Math.round(Number(cfg.close_v || cfg.open_v) * 1000) };
    if (tpl.key === "deconnexion") body.params = { offline_after_s: Number(cfg.minutes) * 60 };
    return body;
  };

  const buildA9Payload = () => {
    const { scope_type, scope_id } = backendScope();
    const base = { severity: cfg.severity || "medium", enabled: true, scope_type, scope_id, duration_s: Number(cfg.duration_min || 0) * 60 };
    if (tpl.key === "zone") {
      const z = zones.find((x) => x.id === cfg.zone_id);
      return { ...base, name: `${cfg.presence === "outside" ? "Hors" : "Dans"} zone ${z ? z.name : ""}`.trim(),
        groups: [{ op: "AND", conditions: [{ metric: "zone", op: cfg.presence, value: cfg.zone_id }] }] };
    }
    if (tpl.key === "contact") {
      return { ...base, name: `Contact ${cfg.ignition === "on" ? "mis" : "coupé"}`,
        groups: [{ op: "AND", conditions: [{ metric: "ignition", op: "==", value: cfg.ignition }] }] };
    }
    if (tpl.key === "horaires") {
      const s = String(cfg.hour_end).padStart(2, "0") + ":00";
      const e = String(cfg.hour_start).padStart(2, "0") + ":00";
      const g1 = { op: "AND", conditions: [{ metric: "moving", op: "==", value: "true" }] };
      const orConds = [];
      if (cfg.days?.length && cfg.days.length < 7) orConds.push({ metric: "weekday", op: "not_in", value: cfg.days.map(String) });
      orConds.push({ metric: "time_window", op: "in", value: { start: s, end: e } });
      const g2 = { op: "OR", conditions: orConds };
      return { ...base, name: `Utilisation hors ${String(cfg.hour_start).padStart(2, "0")}h-${String(cfg.hour_end).padStart(2, "0")}h`,
        groups: [g1, g2] };
    }
    return base;
  };

  // Écrit la préférence de notification (mécanisme existant). PROPAGE l'échec : on ne veut JAMAIS
  // une alerte active sans sa préférence de notification enregistrée. On FUSIONNE avec la config
  // STOCKÉE de la portée : `notify_close` est préservé (absent du wizard) et `recipient_user_ids`
  // est OMIS (le backend conserve les comptes nommés). Seuls les canaux/rôles — pré-remplis depuis
  // l'existant — sont écrits, donc rien n'est réinitialisé sans action explicite de l'utilisateur.
  const applyNotification = async () => {
    const ntype = NOTIF_TYPE[tpl.key];
    if (!ntype) return;
    if (!notif.in_app && !notif.email) return; // aucun canal -> on laisse la config de notification telle quelle
    const { scope_type, scope_id } = backendScope();
    const qs = `scope_type=${scope_type}` + (scope_id ? `&scope_id=${encodeURIComponent(scope_id)}` : "");
    // Relecture FRAÎCHE de la config stockée juste avant l'écriture : élimine toute course avec le
    // pré-remplissage. Si cette lecture échoue, on PROPAGE (rien n'est écrit, pas d'écrasement par défaut).
    const r = await api.get(`/alert-notification-rules?${qs}`);
    const ex = (r.data || []).find((x) => x.type === ntype) || {};
    const body = {
      notify_open: true,                           // activer l'alerte à l'ouverture = but explicite du wizard
      notify_close: !!ex.notify_close,             // préservé (non géré par le wizard)
      channels: { in_app: !!notif.in_app, email: !!notif.email },
      recipient_roles: notif.roles || [],
      // recipient_user_ids OMIS -> le backend conserve les comptes nommés existants
    };
    await api.put(`/alert-notification-rules/${ntype}?${qs}`, body);
  };

  const doCreate = async () => {
    if (!validReglages()) { toast.error("Complétez les réglages (voir « Options avancées »)"); setAdvOpen(true); return; }
    setSaving(true);
    try {
      // Notification D'ABORD (idempotent par type+scope) : si elle échoue, on n'a PAS encore créé la
      // règle -> aucune alerte active sans notification, et le retry ne crée pas de doublon.
      await applyNotification();
      if (tpl.engine === "native") {
        const { scope_type, scope_id } = backendScope();
        const qs = `scope_type=${scope_type}` + (scope_id ? `&scope_id=${encodeURIComponent(scope_id)}` : "");
        await api.put(`/alert-settings/${tpl.key}?${qs}`, buildNativeBody());
      } else {
        await api.post("/alert-rules", buildA9Payload());
      }
      toast.success(tpl.engine === "native"
        ? "Alerte enregistrée ✓ Retrouvez-la et modifiez-la dans « Mes règles »."
        : "Alerte créée ✓ Elle apparaît dans « Mes règles ».");
      onCreated && onCreated();
      onClose();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((typeof d === "string" ? d : d?.message || d?.error)
        || "La notification ou l'alerte n'a pas pu être enregistrée. Rien n'a été créé — réessayez.");
    } finally { setSaving(false); }
  };

  const resetOverride = async () => {
    const { scope_type, scope_id } = backendScope();
    if (scope_type === "global" && !isAdmin) return;
    try {
      const qs = `scope_type=${scope_type}` + (scope_id ? `&scope_id=${encodeURIComponent(scope_id)}` : "");
      await api.delete(`/alert-settings/exces_vitesse/override?${qs}`);
      toast.success("Réglage réinitialisé");
      setCfg((c) => ({ ...c, speed_limit_kmh: "" }));
      await loadEffective();
    } catch { toast.error("Erreur lors de la réinitialisation"); }
  };

  const canNext = () => {
    if (step === 0) return !!tpl;
    if (step === 1) return scopeChosen;
    return true;
  };
  const goNext = () => setStep((s) => Math.min(2, s + 1));
  const goBack = () => setStep((s) => Math.max(0, s - 1));

  const toggleRole = (r) => setNotif((n) => ({ ...n, roles: (n.roles || []).includes(r) ? n.roles.filter((x) => x !== r) : [...(n.roles || []), r] }));

  // Réglages principaux (seuil, durée, zone, horaires). Pour zone/horaires le réglage est intrinsèque -> visible ;
  // pour les autres il est dans « Options avancées » (valeurs par défaut pré-remplies).
  const SettingsBlock = (
    <div className="space-y-4" data-testid="wizard-settings-block">
      {tpl?.key === "exces_vitesse" && (
        <div className="space-y-3">
          <p className="text-sm font-semibold">M'alerter lorsque la vitesse dépasse</p>
          {effective && (
            <div className="rounded-lg bg-[#17A2B8]/10 border border-[#17A2B8]/30 px-3 py-2 text-sm" data-testid="exces-effective">
              <span className="font-bold">Limite actuelle : {effective.value} km/h</span>
              <span className="block text-xs text-muted-foreground">{SOURCE_LABEL[effective.source] || "Seuil effectif"}</span>
            </div>
          )}
          <div className="flex items-end gap-2">
            <input type="number" min="10" max="300" className={BIG} value={cfg.speed_limit_kmh}
              placeholder={effective ? String(effective.value) : "90"} data-testid="field-speed"
              onChange={(e) => setCfg((c) => ({ ...c, speed_limit_kmh: e.target.value }))} />
            <span className="text-lg font-semibold pb-2">km/h</span>
          </div>
          <p className="text-xs text-muted-foreground">Laissez vide pour utiliser le seuil hérité. Modifier enregistre un réglage spécifique à ce périmètre (10–300 km/h).</p>
          {effective?.hasOverride && scopeKind !== "fleet" && (
            <button onClick={resetOverride} data-testid="reset-inheritance" className="text-xs text-[#F5821F] font-semibold hover:underline">Réinitialiser ce réglage (revenir au seuil hérité)</button>
          )}
        </div>
      )}
      {["arret_prolonge", "ralenti", "deconnexion"].includes(tpl?.key) && (
        <div className="space-y-2">
          <p className="text-sm font-semibold">
            {tpl.key === "arret_prolonge" && "M'alerter si le véhicule reste arrêté pendant plus de"}
            {tpl.key === "ralenti" && "M'alerter si le moteur reste au ralenti pendant plus de"}
            {tpl.key === "deconnexion" && "Considérer le traceur hors ligne après"}
          </p>
          <div className="flex items-end gap-2">
            <input type="number" min="1" className={BIG} value={cfg.minutes} data-testid="field-minutes"
              onChange={(e) => setCfg((c) => ({ ...c, minutes: e.target.value }))} />
            <span className="text-lg font-semibold pb-2">minutes{tpl.key === "deconnexion" ? " sans communication" : ""}</span>
          </div>
        </div>
      )}
      {tpl?.key === "batterie" && (
        <div className="space-y-3">
          <p className="text-sm font-semibold">M'alerter lorsque la tension d'alimentation externe descend sous</p>
          <div className="flex items-end gap-2">
            <input type="number" step="0.1" min="6" max="36" className={BIG} value={cfg.open_v} data-testid="field-battery-open"
              onChange={(e) => setCfg((c) => ({ ...c, open_v: e.target.value }))} />
            <span className="text-lg font-semibold pb-2">V</span>
          </div>
          <div className="flex items-end gap-2">
            <span className="text-xs text-muted-foreground pb-2">Considérer rétablie à</span>
            <input type="number" step="0.1" min="6" max="36" className={`${INP} w-24`} value={cfg.close_v} data-testid="field-battery-close"
              onChange={(e) => setCfg((c) => ({ ...c, close_v: e.target.value }))} />
            <span className="text-xs pb-2">V</span>
          </div>
          <p className="text-xs text-muted-foreground">Surveille la tension d'alimentation externe (batterie du véhicule / source d'alimentation du traceur) : alerte quand elle passe sous le seuil. Une tension basse ne prouve pas à elle seule une coupure. Ne concerne pas la batterie de secours interne du traceur.</p>
        </div>
      )}
      {usesSchedule && (
        <div className="pt-2 border-t border-border/60">
          <p className="text-sm font-semibold mb-2">Quand cette alerte est-elle active ?</p>
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="sched-always">
              <input type="radio" name="sched" checked={!!cfg.always_on} onChange={() => setCfg((c) => ({ ...c, always_on: true }))} /> Toujours active — 24h/24, 7j/7
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="sched-hours">
              <input type="radio" name="sched" checked={!cfg.always_on} onChange={() => setCfg((c) => ({ ...c, always_on: false }))} /> Seulement à certaines heures
            </label>
          </div>
          {!cfg.always_on && (
            <div className="space-y-3 pl-6 mt-2">
              <div className="flex flex-wrap gap-1">
                {DAYS.map(([d, lbl]) => {
                  const on = (cfg.days || []).includes(d);
                  return <button type="button" key={d} data-testid={`sched-day-${d}`} onClick={() => setCfg((c) => { const s = new Set(c.days || []); s.has(d) ? s.delete(d) : s.add(d); return { ...c, days: [...s].sort() }; })}
                    className={`text-xs px-3 py-1.5 rounded border ${on ? "bg-[#17A2B8] text-white border-[#17A2B8]" : "border-border text-muted-foreground"}`}>{lbl}</button>;
                })}
              </div>
              <div className="flex items-center gap-2">
                <input type="number" min="0" max="24" className={`${INP} w-20`} value={cfg.hour_start} data-testid="sched-hour-start" onChange={(e) => setCfg((c) => ({ ...c, hour_start: e.target.value }))} />
                <span className="text-sm">h →</span>
                <input type="number" min="0" max="24" className={`${INP} w-20`} value={cfg.hour_end} data-testid="sched-hour-end" onChange={(e) => setCfg((c) => ({ ...c, hour_end: e.target.value }))} />
                <span className="text-sm">h</span>
              </div>
            </div>
          )}
        </div>
      )}
      {usesDuration && (
        <div className="pt-2 border-t border-border/60">
          <p className="text-sm font-semibold mb-2">Déclencher</p>
          <div className="flex flex-wrap gap-2" data-testid="field-duration">
            {[["0", "Immédiatement"], ["0.5", "après 30 s"], ["1", "après 1 min"], ["5", "après 5 min"]].map(([v, lbl]) => (
              <button key={v} type="button" data-testid={`dur-${v}`} onClick={() => setCfg((c) => ({ ...c, duration_min: Number(v) }))}
                className={`text-xs px-3 py-1.5 rounded border ${Number(cfg.duration_min || 0) === Number(v) ? "bg-[#17A2B8] text-white border-[#17A2B8]" : "border-border text-muted-foreground"}`}>{lbl}</button>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  // zone & horaires : réglage intrinsèque -> visible en principal. Autres : repliés dans « Options avancées ».
  const settingsArePrimary = tpl && ["zone", "horaires"].includes(tpl.key);

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-3 sm:p-6" data-testid="simple-alert-wizard" onClick={onClose}>
      <div className="bg-card border border-border rounded-2xl w-full max-w-2xl my-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h2 className="font-extrabold text-lg">Créer une alerte</h2>
          <button onClick={onClose} data-testid="wizard-close" className="text-muted-foreground hover:text-foreground p-1"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex items-center gap-1 px-5 py-3 border-b border-border overflow-x-auto" data-testid="wizard-progress">
          {STEP_LABELS.map((l, i) => (
            <div key={l} className="flex items-center gap-1 shrink-0">
              <span className={`text-[11px] px-2.5 py-1 rounded-full font-bold ${i === step ? "bg-[#17A2B8] text-white" : i < step ? "bg-[#17A2B8]/20 text-[#17A2B8]" : "bg-secondary text-muted-foreground"}`}>{i + 1}. {l}</span>
              {i < STEP_LABELS.length - 1 && <ChevronRight className="w-3 h-3 text-muted-foreground" />}
            </div>
          ))}
        </div>

        <div className="p-5 min-h-[280px]">
          {/* Étape 1 — Situation */}
          {step === 0 && (
            <div className="space-y-4" data-testid="wizard-step-type">
              <div>
                <p className="label-mono mb-2">Situations courantes</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {TEMPLATES.filter((t) => t.common).map((t) => (
                    <button key={t.key} onClick={() => pickTemplate(t)} data-testid={`tpl-${t.key}`}
                      className="text-left border border-border rounded-xl p-4 hover:border-[#17A2B8] hover:bg-[#17A2B8]/5 transition-colors flex gap-3 items-start">
                      <span className="shrink-0 w-10 h-10 rounded-lg bg-[#17A2B8]/15 flex items-center justify-center"><t.icon className="w-5 h-5 text-[#17A2B8]" /></span>
                      <span><span className="font-bold text-sm block">{t.title}</span><span className="text-xs text-muted-foreground">{t.desc}</span></span>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="label-mono mb-2">Autres situations</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {TEMPLATES.filter((t) => !t.common).map((t) => (
                    <button key={t.key} onClick={() => pickTemplate(t)} data-testid={`tpl-${t.key}`}
                      className="text-left border border-border rounded-xl p-4 hover:border-[#17A2B8] hover:bg-[#17A2B8]/5 transition-colors flex gap-3 items-start">
                      <span className="shrink-0 w-10 h-10 rounded-lg bg-secondary flex items-center justify-center"><t.icon className="w-5 h-5 text-muted-foreground" /></span>
                      <span><span className="font-bold text-sm block">{t.title}</span><span className="text-xs text-muted-foreground">{t.desc}</span></span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Étape 2 — Véhicules */}
          {step === 1 && tpl && (
            <div className="space-y-4" data-testid="wizard-step-scope">
              <p className="text-sm font-semibold">Qui souhaitez-vous surveiller ?</p>
              <div className="grid grid-cols-2 gap-2">
                {[["fleet", "Toute ma flotte"], ["company", "Une société"], ["pool", "Un groupe de véhicules"], ["vehicle", "Un véhicule"]]
                  .filter(([k]) => k !== "fleet" || fleetAvailable)
                  .map(([k, lbl]) => (
                    <button key={k} onClick={() => { setScopeKind(k); setScopeId(""); }} data-testid={`scope-${k}`}
                      className={`border rounded-lg px-3 py-3 text-sm font-semibold text-left ${scopeKind === k ? "border-[#17A2B8] bg-[#17A2B8]/10 text-[#17A2B8]" : "border-border hover:border-[#17A2B8]/50"}`}>{lbl}</button>
                  ))}
              </div>
              {scopeKind === "company" && (
                <select className={INP} value={scopeId} onChange={(e) => setScopeId(e.target.value)} data-testid="scope-entity-company">
                  <option value="">— Choisir une société —</option>{companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              )}
              {scopeKind === "pool" && (
                <select className={INP} value={scopeId} onChange={(e) => setScopeId(e.target.value)} data-testid="scope-entity-pool">
                  <option value="">— Choisir un groupe —</option>{pools.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              )}
              {scopeKind === "vehicle" && (
                <select className={INP} value={scopeId} onChange={(e) => setScopeId(e.target.value)} data-testid="scope-entity-vehicle">
                  <option value="">— Choisir un véhicule —</option>{vehicles.map((v) => <option key={v.id} value={v.id}>{v.name} · {v.plate}</option>)}
                </select>
              )}
            </div>
          )}

          {/* Étape 3 — Avertissement (+ réglages essentiels / Options avancées) */}
          {step === 2 && tpl && (
            <div className="space-y-5" data-testid="wizard-step-notify">
              {/* zone : sélection de zone (intrinsèque) */}
              {tpl.key === "zone" && (
                <div className="space-y-3" data-testid="zone-setup">
                  <p className="text-sm font-semibold">Choisir une zone</p>
                  <select className={INP} value={cfg.zone_id} onChange={(e) => setCfg((c) => ({ ...c, zone_id: e.target.value }))} data-testid="field-zone">
                    <option value="">— Choisir une zone —</option>{zones.map((z) => <option key={z.id} value={z.id}>{z.name}</option>)}
                  </select>
                  <div className="space-y-1">
                    <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="zone-inside">
                      <input type="radio" name="presence" checked={cfg.presence === "inside"} onChange={() => setCfg((c) => ({ ...c, presence: "inside" }))} /> M'alerter à l'entrée dans cette zone
                    </label>
                    <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="zone-outside">
                      <input type="radio" name="presence" checked={cfg.presence === "outside"} onChange={() => setCfg((c) => ({ ...c, presence: "outside" }))} /> M'alerter à la sortie de cette zone
                    </label>
                  </div>
                </div>
              )}
              {/* horaires : plage (intrinsèque) */}
              {tpl.key === "horaires" && (
                <div className="space-y-3" data-testid="horaires-setup">
                  <p className="text-sm font-semibold">Horaires autorisés (l'alerte se déclenche en dehors)</p>
                  <div className="flex flex-wrap gap-1" data-testid="field-days">
                    {DAYS.map(([d, lbl]) => {
                      const on = (cfg.days || []).includes(d);
                      return <button type="button" key={d} data-testid={`day-${d}`} onClick={() => setCfg((c) => { const s = new Set(c.days || []); s.has(d) ? s.delete(d) : s.add(d); return { ...c, days: [...s].sort() }; })}
                        className={`text-xs px-3 py-1.5 rounded border ${on ? "bg-[#17A2B8] text-white border-[#17A2B8]" : "border-border text-muted-foreground"}`}>{lbl}</button>;
                    })}
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="number" min="0" max="23" className={`${INP} w-20`} value={cfg.hour_start} data-testid="field-hour-start" onChange={(e) => setCfg((c) => ({ ...c, hour_start: e.target.value }))} />
                    <span className="text-sm">h → </span>
                    <input type="number" min="0" max="24" className={`${INP} w-20`} value={cfg.hour_end} data-testid="field-hour-end" onChange={(e) => setCfg((c) => ({ ...c, hour_end: e.target.value }))} />
                    <span className="text-sm">h</span>
                  </div>
                </div>
              )}
              {tpl.key === "contact" && (
                <div className="space-y-2" data-testid="contact-setup">
                  <p className="text-sm font-semibold">Être alerté lors d'une</p>
                  <select className={INP} value={cfg.ignition} onChange={(e) => setCfg((c) => ({ ...c, ignition: e.target.value }))} data-testid="field-contact">
                    <option value="on">mise du contact (moteur démarré)</option>
                    <option value="off">coupure du contact (moteur arrêté)</option>
                  </select>
                </div>
              )}

              {/* Comment être averti */}
              <div className="space-y-3">
                <p className="text-sm font-semibold flex items-center gap-2"><Bell className="w-4 h-4 text-[#17A2B8]" /> Comment être averti ?{!notifLoaded && <span className="text-[11px] font-normal text-muted-foreground" data-testid="notif-loading">chargement de la configuration…</span>}</p>
                <div className="flex flex-wrap gap-2">
                  <button type="button" disabled={!notifLoaded} onClick={() => setNotif((n) => ({ ...n, in_app: !n.in_app }))} data-testid="notif-channel-inapp"
                    className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg border disabled:opacity-40 ${notif.in_app ? "border-[#17A2B8] bg-[#17A2B8]/10 text-[#17A2B8]" : "border-border text-muted-foreground"}`}>
                    <Bell className="w-4 h-4" /> Dans l'application
                  </button>
                  <button type="button" disabled={!notifLoaded} onClick={() => setNotif((n) => ({ ...n, email: !n.email }))} data-testid="notif-channel-email"
                    className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg border disabled:opacity-40 ${notif.email ? "border-[#F59E0B] bg-[#F59E0B]/10 text-[#F59E0B]" : "border-border text-muted-foreground"}`}>
                    <Mail className="w-4 h-4" /> Par email
                  </button>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-bold mb-1.5">Destinataires</p>
                  <div className="flex gap-4">
                    {[["admin", "Administrateurs"], ["gestionnaire", "Gestionnaires"]].map(([r, lbl]) => (
                      <label key={r} className={`flex items-center gap-2 text-sm ${notifLoaded ? "cursor-pointer" : "opacity-40 cursor-not-allowed"}`}>
                        <input type="checkbox" disabled={!notifLoaded} checked={(notif.roles || []).includes(r)} data-testid={`notif-role-${r}`} onChange={() => toggleRole(r)} /> {lbl}
                      </label>
                    ))}
                  </div>
                </div>
                {!notif.in_app && !notif.email && <p className="text-xs text-muted-foreground">Aucun canal sélectionné : les préférences de notification existantes du périmètre sont conservées.</p>}
              </div>

              {/* Réglages essentiels (zone/horaires affichés plus haut) OU Options avancées */}
              {settingsArePrimary ? (
                <details className="border border-border rounded-lg" open={advOpen} onToggle={(e) => setAdvOpen(e.target.open)} data-testid="wizard-advanced">
                  <summary className="cursor-pointer px-4 py-2.5 text-sm font-semibold flex items-center gap-2 text-muted-foreground"><Settings2 className="w-4 h-4" /> Options avancées</summary>
                  <div className="px-4 pb-4 pt-1">{SettingsBlock}</div>
                </details>
              ) : (
                <details className="border border-border rounded-lg" open={advOpen} onToggle={(e) => setAdvOpen(e.target.open)} data-testid="wizard-advanced">
                  <summary className="cursor-pointer px-4 py-2.5 text-sm font-semibold flex items-center gap-2 text-muted-foreground"><Settings2 className="w-4 h-4" /> Options avancées (seuils, durées, horaires)</summary>
                  <div className="px-4 pb-4 pt-1">{SettingsBlock}</div>
                </details>
              )}

              <div className="rounded-xl bg-[#17A2B8]/10 border border-[#17A2B8]/40 p-4">
                <p className="text-sm font-bold leading-snug" data-testid="wizard-summary">{summary()}</p>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between px-5 py-4 border-t border-border">
          <button onClick={step === 0 ? onClose : goBack} data-testid="wizard-back" className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground px-3 py-2">
            <ChevronLeft className="w-4 h-4" /> {step === 0 ? "Annuler" : "Précédent"}
          </button>
          {step < 2 ? (
            <button onClick={goNext} disabled={!canNext()} data-testid="wizard-next"
              className="flex items-center gap-1 bg-[#17A2B8] hover:bg-[#1391a5] disabled:opacity-40 text-white font-semibold px-5 py-2 rounded-md text-sm">
              Continuer <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button onClick={doCreate} disabled={saving || !validReglages() || ((notif.in_app || notif.email) && !notifLoaded)} data-testid="wizard-create"
              className="flex items-center gap-1 bg-[#17A2B8] hover:bg-[#1391a5] disabled:opacity-40 text-white font-semibold px-5 py-2 rounded-md text-sm">
              <Check className="w-4 h-4" /> Créer l'alerte
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
